import arrowLeftLargeTokens from '@amzn/meridian-tokens/base/icon/arrow-left-large';
import Alert from '@amzn/meridian/alert';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Checkbox from '@amzn/meridian/checkbox';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Link from '@amzn/meridian/link';
import Loader from '@amzn/meridian/loader';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Responsive from '@amzn/meridian/responsive';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Toaster from '@amzn/meridian/toaster';
import { Form, Formik, FormikProps, useFormikContext } from 'formik';
import React, {
  FormEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams, useLocation, useNavigate } from 'react-router';
import queryString from 'qs';

import DataSource from '../../api';
import {
  CoachingFormData,
  CoachingFormInputData,
  defaultCoachingSessionValues,
  DeliveredStatusObject,
  enumEmpty,
  FollowedSecurityPolicyOptionsObject,
} from '../../api/CoachingForm/schema';
import { StateContext } from '../../GlobalStateStore';
import { getLocaleString } from '../../translations/index';
import { EmptyProfile, Profile } from '../../Types';
import DataState from '../../Types/DataState';
import { COACHING_TEXT_LIMIT, getCurrentWeek } from '../../Util';
import { Feature, isFeatureEnabled } from '../../config/FeaturesConfig';
import { renderUrl, urls } from '../../config/Url';
import useCustomTracking from '../../Util/useCustomTracking';
import AssociateInfo from './AssociateInfo/AssociateInfo';
import AutoSaveFormik from './AutoSave/AutoSaveFormik';
import TimeoutToast from './AutoSave/TimeoutToast';
import * as CoachingDetails from './CoachingDetails/Container';
import * as AddGoals from './AddGoals/Container';
import styles from './CoachingForm.module.css';
import * as CoachingSession from './CoachingSession/FormPanel';
import validationSchema from './Context';
import { initialCoachingValues } from './Data';
import PanelBox from './LayoutComponents/PanelBox';
import trimCoachingData from './trimCoachingData';
import FeatureConfig from '../../config/FeaturesConfig';
import CROContainer from './ContactReview/ContactReviewOverhaul/CROContainer';
import moment from 'moment';
import GrowModel from './GrowModel/GrowModel';

interface FormSubmitProps {
  emailCheck: boolean;
  noteId: string;
  //TODO - Remove this logic after PRIME DAY
  vertical: string;
  selectedGoals: string[];
}

const CoachingFormSubmit: React.FC<FormSubmitProps> = ({
  emailCheck,
  noteId,
  //TODO - Remove this logic after PRIME DAY
  vertical,
  selectedGoals,
}: FormSubmitProps): React.ReactElement => {
  const intl = useIntl();
  const {
    values,
    handleSubmit,
    dirty,
    initialValues,
    errors,
  }: FormikProps<CoachingFormData> = useFormikContext();
  // Do not use Formik isValid. It doesn't evaluate initialValues.
  const isValid = validationSchema(intl).isValidSync(
    values || initialCoachingValues
  );
  const [submitSaveState, setSubmitSaveState] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [showValidationFailToast, setShowValidationFailToast] = useState(false);
  const [showContactReviewMissingToast, setshowContactReviewMissingToast] =
    useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [usedContactId, setUsedContactId] = useState<string[]>([]);
  const [missedContactId, setMissedContactId] = useState<
    (string | undefined)[]
  >([]);
  const [missedContactIds, setMissedContactIds] = useState<string | undefined>(
    ''
  );
  const [isContactReviewMissing, setIsContactReviewMissing] = useState(false);
  const [ModalCloseTrackingTag, modalCloseTrackClick] = useCustomTracking(
    'coaching-form-confirm-deliver-session-modal-close',
    'coaching-form-confirm-deliver-session-modal-close',
    {
      'data-csa-c-type': 'modal',
    }
  );
  const [ModalOpenTrackingTag, modalOpenTrackClick] = useCustomTracking(
    'coaching-form-confirm-deliver-session-modal-open',
    'coaching-form-confirm-deliver-session-modal-open',
    {
      'data-csa-c-type': 'modal',
    }
  );
  const messageTimeout = 4000;
  const validationFailToast = [
    {
      id: 'Coaching_Validation_Toast',
      timeout: messageTimeout,
    },
  ];
  const contactReviewMissingToast = [
    {
      id: 'Contact_Review_Missing_Toast',
      timeout: messageTimeout,
    },
  ];
  const onModalOpen = useCallback(() => {
    modalCloseTrackClick();
    setModalOpen(true);
  }, []);
  const onModalClose = useCallback(() => {
    modalOpenTrackClick();
    setModalOpen(false);
  }, []);
  const onCloseValidationFailToast = useCallback(() => {
    setShowValidationFailToast(false);
  }, []);
  const onCloseContactReviewMissingToast = useCallback(() => {
    setshowContactReviewMissingToast(false);
  }, []);

  const apiNaws = DataSource.getDataFeed('NAWS');
  const isNotesExceedLength =
    values.coachingSession.coachingSummaryNotes.length > COACHING_TEXT_LIMIT;

  const handleSave = async (): Promise<void> => {
    if (isNotesExceedLength) return;

    setShowToast(true);

    //TODO - Remove this logic after PRIME DAY
    values.contactReview.forEach(review => {
      if (!review.followedSecurityPolicies) {
        review.followedSecurityPolicies =
          FollowedSecurityPolicyOptionsObject.NA;
      }
      if (!review.vertical && vertical !== '') {
        review.vertical = vertical;
      }
    });

    const newValues: CoachingFormInputData = trimCoachingData(values);

    apiNaws.updateCoachingNote(
      { ...newValues, goalIDs: selectedGoals },
      setSubmitSaveState
    );
  };

  useEffect(() => {
    if (dirty) {
      setUsedContactId(
        values.coachingDetails.map(d => d.relatedContacts).flat()
      );
    } else {
      setUsedContactId(
        initialValues.coachingDetails.map(d => d.relatedContacts).flat()
      );
    }
  }, [values, initialValues]);

  useEffect(() => {
    if (dirty) {
      setMissedContactId(
        values.contactReview
          .filter(cr => !usedContactId.includes(cr.cscContactID))
          .map(cr => cr.cscContactID)
      );
    } else {
      setMissedContactId(
        initialValues.contactReview
          .filter(cr => !usedContactId.includes(cr.cscContactID))
          .map(cr => cr.cscContactID)
      );
    }
  }, [usedContactId, initialValues.contactReview, values.contactReview]);

  useEffect(() => {
    setIsContactReviewMissing(missedContactId.length > 0);
    if (missedContactId.length > 0) {
      setMissedContactIds(missedContactId.join(', '));
    }
  }, [missedContactId]);

  return (
    <>
      <Row alignmentHorizontal='end'>
        <AutoSaveFormik
          showToast={showToast}
          onSaveDraft={async (): Promise<void> => {
            await handleSave();
          }}
        />
        <Button
          onClick={async (): Promise<void> => {
            await handleSave();
          }}
          type='secondary'
          data-csa-c-type='button'
          data-csa-c-slot-id='save-draft-button-slot'
          data-csa-c-content-id='save-draft-button'
          data-csa-c-noteid={noteId}
          data-csa-c-tag1='Team'
          data-csa-c-tag2='Coaching Form'
          data-csa-c-casa-id={noteId.split('|')[0]}
          data-cy='save-coaching-note'
          disabled={isNotesExceedLength}
        >
          <FormattedMessage
            id='cs_perfportal_coaching_save_draft'
            defaultMessage='Save draft'
            description='Save draft button'
          />
        </Button>
        <Button
          data-csa-c-type='button'
          data-csa-c-slot-id='deliver-coaching-button-slot'
          data-csa-c-content-id='deliver-coaching-button'
          data-cy='deliver-coaching-note'
          onClick={
            isValid && !isContactReviewMissing
              ? (): void => {
                  onModalOpen();
                }
              : // If invalid, the handler triggers validation messages
                (e: FormEvent<HTMLFormElement>): void => {
                  if (isContactReviewMissing) {
                    setshowContactReviewMissingToast(true);
                  } else {
                    handleSubmit(e);
                    setShowValidationFailToast(true);
                  }
                }
          }
          disabled={isNotesExceedLength}
        >
          <FormattedMessage
            id='cs_perfportal_coaching_deliver'
            defaultMessage='Deliver'
            description='Deliver button'
          />
        </Button>
        <TimeoutToast
          show={showToast}
          message={
            (submitSaveState === DataState.LOADED &&
              intl.formatMessage({
                id: 'cs_perfportal_coaching_saved_draft_success',
                defaultMessage: 'Draft successfully saved.',
              })) ||
            (submitSaveState === DataState.ERROR &&
              intl.formatMessage({
                id: 'cs_perfportal_error_reason_coaching_expired',
                defaultMessage:
                  'This coaching has expired. Please cut us a ticket to delete it and start a new one.',
              })) ||
            ''
          }
          duration={3000}
          onClose={(): void => {
            setShowToast(false);
          }}
        />
        {showValidationFailToast && validationFailToast !== undefined && (
          <Toaster
            toasts={validationFailToast}
            onCloseToast={onCloseValidationFailToast}
            alignmentHorizontal='center'
          >
            {(): React.ReactElement => (
              <Alert type='error' toast>
                <FormattedMessage
                  id='cs_perfportal_coaching_validation_failed'
                  defaultMessage='Please fill in missing fields in Coaching Details or Contact Review.'
                />
              </Alert>
            )}
          </Toaster>
        )}
        {showContactReviewMissingToast &&
          isContactReviewMissing &&
          contactReviewMissingToast !== undefined && (
            <Toaster
              toasts={contactReviewMissingToast}
              onCloseToast={onCloseContactReviewMissingToast}
              alignmentHorizontal='center'
            >
              {(): React.ReactElement => (
                <Alert type='error' toast>
                  <FormattedMessage
                    id='cs_perfortal_fill_in_details_or_remove'
                    defaultMessage='Please fill in the contact details for contact ID: {missedContactIds} or remove the contact review to submit the form.'
                    values={{ missedContactIds }}
                  />
                </Alert>
              )}
            </Toaster>
          )}
        <ModalCloseTrackingTag />
        <ModalOpenTrackingTag />
        <Modal
          title={intl.formatMessage({
            id: 'cs_perfportal_coaching_confirm_delivery_of_session',
            defaultMessage: 'Confirm delivery of coaching session',
          })}
          open={modalOpen}
          onClose={onModalClose}
          scrollContainer='viewport'
          closeLabel={intl.formatMessage({
            id: 'cs_perfportal_popover_close',
            defaultMessage: 'close',
          })}
          width={900}
          aria-describedby='modal-confirm-deliver-session'
        >
          {!emailCheck ? (
            <Text id='modal-confirm-deliver-session'>
              <FormattedMessage
                id='cs_perfportal_coaching_confirm_delivery_of_session_description'
                defaultMessage={
                  'By delivering the information in this form, you acknowledge that you have conducted the ' +
                  'coaching session with the associate. Note: Coaching tactic, Coaching method, Duration, ' +
                  'and Preparation time will be excluded.'
                }
              />
            </Text>
          ) : (
            <Text id='modal-confirm-deliver-email-session'>
              <FormattedMessage
                id='cs_perfportal_coaching_confirm_delivery_of_session_description_with_email'
                defaultMessage={
                  'By hitting Deliver you acknowledge that you have conducted the coaching ' +
                  'session and the recipient will be e-mailed a copy of the coaching notes, ' +
                  'excluding private notes and prep time.'
                }
              />
            </Text>
          )}
          <ModalFooter>
            <Row alignmentHorizontal='end' widths='fit'>
              <Button
                type='secondary'
                size='small'
                data-csa-c-type='button'
                data-csa-c-slot-id='deliver-coaching-confirmation-modal-button-slot'
                data-csa-c-content-id='deliver-coaching-confirmation-modal-cancel-button'
                data-csa-c-tag1='Team'
                data-csa-c-tag2='Coaching Form'
                data-csa-c-profile-id={noteId.split('|')[0]}
                onClick={onModalClose}
              >
                <FormattedMessage
                  id='cs_perfportal_cancel'
                  defaultMessage='Cancel'
                />
              </Button>
              <Button
                type='primary'
                size='small'
                data-csa-c-type='button'
                data-csa-c-slot-id='deliver-coaching-confirmation-modal-button-slot'
                data-csa-c-content-id='deliver-coaching-confirmation-modal-confirm-button'
                data-csa-c-tag1='Team'
                data-csa-c-tag2='Coaching Form'
                data-csa-c-noteid={noteId}
                data-csa-c-profile-id={noteId.split('|')[0]}
                data-cy='confirm-deliver-coaching-note'
                onClick={(e: FormEvent<HTMLFormElement>): void => {
                  handleSubmit(e);
                  onModalClose();
                }}
              >
                <FormattedMessage
                  id='cs_perfportal_confirm'
                  defaultMessage='Confirm'
                />
              </Button>
            </Row>
          </ModalFooter>
        </Modal>
      </Row>
    </>
  );
};

const CoachingForm: React.FC = (): React.ReactElement => {
  const intl = useIntl();
  const apiNaws = DataSource.getDataFeed('NAWS');

  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = queryString.parse(location.search.substring(1));
  const isEdit = Boolean(queryParams.isEdit as string);

  const { userId } = useParams() as { userId: string };
  const globalState = useContext(StateContext);
  const { inspireOptions } = globalState.state;
  const { cscEntityPrefix } = inspireOptions.data;

  const [submitDeliverState, setSubmitDeliverState] = useState('');
  const [checked, setChecked] = useState(false);
  const [contactList, setContactList] = useState([]);
  const [selectedGoals, setSelectedGoals] = useState<string[]>([]);
  const [contactListState, setContactListState] = useState(DataState.LOADING);
  const WEEKS_FOR_CONTACTS = 4;
  // GGGG refers to the year of the week. This may be different from YYYY
  // When part of the week is from 2024 and part of it is from 2025. e.g. 2024-12-30 (Monday)
  // In such cases, GGGG refers to the new year (e..g 2025) and YYYY refers to the previous year (e.g. 2024).
  const startWeek = moment()
    .subtract(WEEKS_FOR_CONTACTS, 'w')
    .format('GGGG-WW');
  const endWeek = moment().format('GGGG-WW');
  const emailCoachingNotificationEnabled = isFeatureEnabled(
    Feature.EMAIL_COACHING_NOTIFICATION
  );
  const caseManagementEnabled = isFeatureEnabled(Feature.CASE_MANAGEMENT);
  // NOTE: GROW_MODEL can be deprecated
  const growModelEnabled = isFeatureEnabled(Feature.GROW_MODEL);
  const goalSettingEnabled = isFeatureEnabled(Feature.GOAL_SETTING);

  const [coachingNote, setCoachingNote] = useState<CoachingFormData>({
    id: '',
    coach: {
      userID: '',
      userName: '',
      firstName: '',
      fullName: '',
      isManager: false,
      employmentStartDate: '',
      lastCoachedDate: '',
    },
    recipient: {
      userID: '',
      userName: '',
      firstName: '',
      fullName: '',
      isManager: false,
      employmentStartDate: '',
      lastCoachedDate: '',
    },
    createdAt: '',
    updatedAt: '',
    deliveredAt: '',
    isDelivered: enumEmpty,
    contactReview: [],
    coachingDetails: [],
    coachingSession: defaultCoachingSessionValues,
    reason: enumEmpty,
    otherReason: '',
    goals: [],
  });
  const [associateLevelGoals, setAssociateLevelGoals] = useState([]);
  const [associateLevelGoalsState, setAssociateLevelGoalsState] = useState(
    DataState.INITIALIZED
  );
  const [coachingNoteState, setCoachingNoteState] = useState(DataState.LOADING);
  const [profileState, setProfileState] = useState(DataState.LOADING);
  const [showToast, setShowToast] = useState(false);
  const [showMetricToast, setMetricShowToast] = useState(false);

  const startTime = new Date(2024, 4, 28).toISOString();
  const endTime = new Date(2024, 6, 5).toISOString();
  const [LHDefaultVertical, setLHDefaultVertical] = useState('');
  const [LHDefaultVerticalState, setLHDefaultVerticalState] = useState(
    DataState.LOADING
  );

  const messageTimeout = 2000;

  const onCloseToast = useCallback(() => {
    setShowToast(false);
  }, []);

  const onCloseMetricToast = useCallback(() => {
    setMetricShowToast(false);
  }, []);

  const [profile, setProfile] = useState<Profile>(EmptyProfile);

  useEffect(() => {
    if (goalSettingEnabled && profileState === DataState.LOADED) {
      const queryGetAssociateLevelGoals =
        apiNaws.prepareQueryforGetAssociateLevelGoals(
          `${cscEntityPrefix}:${profile.user.userID}`,
          [`${cscEntityPrefix}:${profile.user.userID}`]
        );

      apiNaws.getAssociateLevelGoals(
        queryGetAssociateLevelGoals,
        setAssociateLevelGoals,
        setAssociateLevelGoalsState
      );
    }
  }, [profileState]);

  // //TODO - Remove this logic after PRIME DAY
  useEffect(() => {
    if (profile.user.userID === '' || profileState === DataState.LOADING) {
      return;
    }

    if (
      coachingNote.createdAt > startTime &&
      coachingNote.createdAt < endTime &&
      LHDefaultVerticalState === DataState.LOADING
    ) {
      const queryVariables = apiNaws.prepareLighthouseDefaultVerticalQuery(
        profile.user.userID,
        cscEntityPrefix
      );

      apiNaws.getDefaultVertical(
        queryVariables,
        setLHDefaultVertical,
        setLHDefaultVerticalState
      );
    }
  }, [coachingNote, profile, profileState, LHDefaultVerticalState]);

  useEffect(() => {
    if (inspireOptions.state === DataState.LOADED && userId) {
      const queryCoachVariables = apiNaws.prepareQuery(
        getCurrentWeek(),
        userId,
        cscEntityPrefix
      );
      apiNaws.getDraftCoachingNote(
        queryCoachVariables,
        setCoachingNote,
        setCoachingNoteState
      );
    }
  }, [inspireOptions.state, userId]);

  useEffect(() => {
    if (coachingNoteState === DataState.LOADED) {
      if (coachingNote.goals?.length) {
        setSelectedGoals(coachingNote.goals.map(goal => goal.goalID));
      }
    }
  }, [coachingNoteState]);

  useEffect(() => {
    async function onLoad(userId: string): Promise<void> {
      await FeatureConfig.refreshUserConfig(userId);
    }

    if (inspireOptions.state === DataState.LOADED) {
      onLoad(userId);
    }
  }, [inspireOptions.state, userId]);

  useEffect(() => {
    if (inspireOptions.state === DataState.LOADED && userId) {
      const queryMigrationVariables = apiNaws.prepareQuery(
        getCurrentWeek(),
        userId,
        cscEntityPrefix
      );
      apiNaws.getProfileData(
        queryMigrationVariables,
        setProfile,
        setProfileState
      );
    }
  }, [inspireOptions.state, userId]);

  useEffect(() => {
    const { userName } = profile.user;
    if (userName !== '' && inspireOptions.state === DataState.LOADED) {
      const userID = userId ? `${cscEntityPrefix}:${userId}` : '';
      const queryVariablesNaws = apiNaws.prepareQueryforRecommendationsGetter(
        userID,
        userName,
        startWeek,
        endWeek
      );
      apiNaws.getConsolidatedContacts(
        queryVariablesNaws,
        setContactList,
        setContactListState
      );
    }
  }, [inspireOptions.state, userId, profile]);

  if (
    coachingNoteState === DataState.LOADING ||
    profileState === DataState.LOADING
  ) {
    return (
      <div style={{ textAlign: 'center' }}>
        <Loader type='circular' size='large' />
      </div>
    );
  }

  const windowHref = (): void => {
    window.location.href = renderUrl(`${urls.coachingNotes}/${userId}`);
  };

  const delayRedirect = (): void => {
    windowHref();
  };

  const deliveredToast = [
    {
      id: 'Coaching_Delivered_Toast',
      timeout: messageTimeout,
    },
  ];

  const metricToast = [
    {
      id: 'Coaching_Metric_Toast',
      timeout: messageTimeout,
    },
  ];

  const handleDeliver = async (values: CoachingFormData): Promise<void> => {
    setShowToast(true);

    //TODO - Remove this logic after PRIME DAY
    values.contactReview.forEach(review => {
      if (!review.followedSecurityPolicies) {
        review.followedSecurityPolicies =
          FollowedSecurityPolicyOptionsObject.NA;
      }
      if (!review.vertical) {
        review.vertical = LHDefaultVertical;
      }
    });

    const newValues: CoachingFormData = {
      ...values,
      isDelivered: caseManagementEnabled
        ? DeliveredStatusObject.OPEN_DELIVERED
        : DeliveredStatusObject.DELIVERED,
    };

    const trimmedValues: CoachingFormInputData = trimCoachingData(newValues);
    await apiNaws.updateCoachingNote(
      { ...trimmedValues, goalIDs: selectedGoals },
      setSubmitDeliverState,
      getLocaleString(),
      checked
    );
    delayRedirect();
  };

  if (submitDeliverState === DataState.LOADING) {
    return (
      <div style={{ textAlign: 'center' }}>
        <Loader type='circular' size='large' />
      </div>
    );
  }

  const isGoUser = profile.user.owner !== 'Amazon';

  return (
    <Formik
      initialValues={coachingNote}
      validationSchema={validationSchema(intl)}
      onSubmit={handleDeliver}
    >
      {(): React.ReactElement => (
        <Form>
          <Column>
            {growModelEnabled && <GrowModel />}
            <PanelBox
              heading={intl.formatMessage({
                id: 'cs_perfportal_coaching_associate_information',
                description: 'Associate information panel heading',
                defaultMessage: 'Associate information',
              })}
            >
              <AssociateInfo initialValues={coachingNote} profile={profile} />
            </PanelBox>

            <PanelBox
              heading={intl.formatMessage({
                id: 'cs_perfportal_add_contacts',
                description: 'Contact review overhaul panel heading',
                defaultMessage: 'Add contacts',
              })}
              blurb={intl.formatMessage({
                id: 'cs_perfportal_coaching_contact_review_overhaul_blurb_required_for_contact_quality',
                description: 'Contact review overhaul panel description',
                defaultMessage:
                  "Select from the contacts you've reviewed for this associate",
              })}
            >
              <CROContainer
                contactList={contactList}
                contactListState={contactListState}
                setShowToast={setMetricShowToast}
              />
            </PanelBox>
            <PanelBox
              heading={intl.formatMessage({
                id: 'cs_perfportal_coaching_details',
                defaultMessage: 'Coaching Details',
              })}
              blurb={intl.formatMessage({
                id: 'cs_perfportal_coaching_detail_blurb_at_least_one_required',
                defaultMessage:
                  'Choose at least one topic with an assigned action.',
              })}
            >
              <CoachingDetails.Container
                contactList={contactList}
                isEdit={isEdit}
                userId={userId}
              />
            </PanelBox>
            {goalSettingEnabled && (
              <PanelBox
                heading={intl.formatMessage({
                  id: 'cs_perfportal_add_goals',
                  description: 'Goal panel heading',
                  defaultMessage: 'Add a Goal',
                })}
                blurb={intl.formatMessage({
                  id: 'cs_perfportal_add_goals_blurb',
                  description: 'Goal panel description',
                  defaultMessage: 'Choose associate’s goals',
                })}
              >
                <AddGoals.Container
                  goalsState={associateLevelGoalsState}
                  goals={associateLevelGoals}
                  disabled={
                    coachingNote.isDelivered !== DeliveredStatusObject.DRAFT &&
                    coachingNote.isDelivered !== DeliveredStatusObject.UNDEFINED
                  }
                  selectedGoals={selectedGoals}
                  setSelectedGoals={setSelectedGoals}
                  noteId={coachingNote.id}
                />
              </PanelBox>
            )}
            <PanelBox
              heading={intl.formatMessage({
                id: 'cs_perfportal_coaching_session',
                description: 'Coaching session panel heading',
                defaultMessage: 'Coaching session',
              })}
              blurb={intl.formatMessage({
                id: 'cs_perfportal_coaching_session_blurb_how_when_to_coach_associate',
                description: 'Coaching session panel description',
                defaultMessage:
                  'Define how and when you want to coach the associate.',
              })}
            >
              <CoachingSession.FormPanel />
            </PanelBox>
            {!(isGoUser || !emailCoachingNotificationEnabled) && (
              <Checkbox
                checked={checked}
                onChange={setChecked}
                data-csa-c-type='element'
                data-csa-c-slot-id='coaching-form-email-notification-slot'
                data-csa-c-content-id='coaching-form-email-notification-checkbox'
                data-csa-c-checked={`${checked}`}
              >
                <FormattedMessage
                  id='cs_perfportal_coaching_form_email_notification'
                  defaultMessage='E-mail coaching summary to associate'
                />
              </Checkbox>
            )}
            {showToast && deliveredToast !== undefined && (
              <Toaster
                toasts={deliveredToast}
                onCloseToast={onCloseToast}
                alignmentHorizontal='center'
              >
                {(): React.ReactElement => (
                  <Alert type='success' toast>
                    {submitDeliverState === DataState.LOADED && (
                      <FormattedMessage
                        id='cs_perfportal_coaching_delivered_coaching_success'
                        defaultMessage='Coaching delivered.'
                      />
                    )}
                  </Alert>
                )}
              </Toaster>
            )}
            {showMetricToast && metricToast !== undefined && (
              <Toaster
                toasts={metricToast}
                onCloseToast={onCloseMetricToast}
                alignmentHorizontal='center'
              >
                {(): React.ReactElement => (
                  <Alert type='success' toast>
                    <FormattedMessage
                      id='cs_perfportal_coaching_metric_update_success'
                      defaultMessage='Metric Updated Successfully.'
                    />
                  </Alert>
                )}
              </Toaster>
            )}
            {submitDeliverState === DataState.ERROR && (
              <Alert type='error'>
                <FormattedMessage
                  id='cs_perfportal_error_reason_coaching_expired'
                  defaultMessage='This coaching has expired. Please cut us a ticket to delete it and start a new one.'
                />
              </Alert>
            )}
            <CoachingFormSubmit
              emailCheck={checked}
              noteId={coachingNote.id}
              vertical={LHDefaultVertical}
              selectedGoals={selectedGoals}
            />
          </Column>
        </Form>
      )}
    </Formik>
  );
};

const CoachingFormWrapper: React.FC = (): React.ReactElement => {
  const { userId } = useParams() as { userId: string };
  const navigate = useNavigate();

  return (
    <Responsive
      query='min-width'
      props={{
        isBig: { default: false, '1330px': true },
      }}
    >
      {({ isBig }): React.ReactElement => (
        <Box>
          <Column
            className={styles.header}
            spacingInset='none'
            width={isBig ? '50%' : '100%'}
          >
            <Row>
              <Icon tokens={arrowLeftLargeTokens} />
              <Link
                href={renderUrl(`${urls.coachingNotes}/${userId}`)}
                onClick={navigate}
                type='secondary'
                data-csa-c-type='element'
                data-csa-c-slot-id='coaching-form-navigation-slot'
                data-csa-c-content-id='coaching-form-navigation-back-link'
              >
                <FormattedMessage
                  id='cs_perfportal_navigate_back'
                  defaultMessage='Back'
                  description='Link to go back'
                />
              </Link>
            </Row>
            <Row spacingInset='none' spacing='none'>
              <Text type='h400'>
                <FormattedMessage
                  id='cs_perfportal_coaching_template'
                  defaultMessage='Coaching template'
                  description='Coaching Form Heading'
                />
              </Text>
            </Row>
            <Row>
              <Text type='b300'>
                <FormattedMessage
                  id='cs_perfportal_coaching_is_an_opportunity'
                  defaultMessage='Coaching is an opportunity to build trust and demonstrate support for your associates.'
                />
              </Text>
            </Row>

            <Row>
              <Text type='b300' color='secondary'>
                <FormattedMessage
                  id='cs_perfportal_coaching_announcement_autosave'
                  defaultMessage='This form is auto-saved.'
                  description='AutoSave Announcement'
                />{' '}
                <FormattedMessage
                  id='cs_perfportal_coaching_announcement_asterisks_indicate_required'
                  defaultMessage='Asterisks (*) indicate required fields'
                  description='Required Fields Announcement'
                />
              </Text>
            </Row>
          </Column>
          <Column
            spacingInset='400 none'
            className={styles.body}
            width={isBig ? '50%' : '100%'}
            spacing='none'
          >
            <CoachingForm />
          </Column>
        </Box>
      )}
    </Responsive>
  );
};

export default CoachingFormWrapper;
